import { useState, useEffect, useCallback, useRef } from "react";
import { useUrlState } from "@/lib/useUrlState";
import { useNavHistory } from "@/fieldops/context/NavHistoryContext";
import { bindNavigate } from "@/estimating/lib/navigation";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "@/App.css";
import { Toaster, toast } from "sonner";
import KeyboardNav from "./components/KeyboardNav";
import { PipelineHeader } from "@/components/PipelineHeader";
import { PipelineTable } from "@/components/PipelineTable";
import ContractsPage from "@/components/ContractsPage";
import ContractAdminApp from "@/components/contract-admin/ContractAdminApp";
import DashboardPage from "@/components/DashboardPage";
import BasisOfDesignPage from "@/sales/BasisOfDesignPage";
import axios from "axios";
// Modules that use raw axios (Budgets, Basis of Design) must be authenticated from the first page too.
{ const t = localStorage.getItem("auth_token"); if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`; }
window.addEventListener("storage", () => { const t = localStorage.getItem("auth_token"); if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`; });
import AccountsPage from "@/components/AccountsPage";
import ReconcilePage from "@/components/ReconcilePage";
import SettingsShell from "@/components/SettingsShell";
import LoginPage from "@/components/LoginPage";
import MiraWidget from "@/components/MiraWidget";
import Sidebar from "@/components/Sidebar";
import EstimatingStub from "@/components/EstimatingStub";
import PageHeader from "@/components/PageHeader";
import PriceListPage from "@/estimating/PriceListPage";
import BidBoardPage from "@/estimating/BidBoardPage";
import ItbInboxPage from "@/estimating/ItbInboxPage";
import CommandPalette from "@/components/CommandPalette";
import BudgetApp from "@/budget/BudgetApp";
import SubBuilderApp from "@/subbuilder/SubBuilderApp";
import OperationsProjectsPage from "@/operations/ProjectsPage";
import OpsDashboardPage from "./operations/OpsDashboardPage";
import PurchasingPage from "@/operations/PurchasingPage";
import BillingDashboardPage from "@/operations/BillingDashboardPage";
import ReceivablesPage from "@/operations/ReceivablesPage";
import RetentionPage from "@/operations/RetentionPage";
import CloseoutPage from "@/operations/CloseoutPage";
import FieldOpsApp from "@/fieldops/FieldOpsApp";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { defaultAmFor } from "@/lib/amDefault";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { TAB_PATH } from "@/lib/tabPaths";

const ESTIMATING_STUBS = { doclibrary: true };

const TAB_AREA = {
  dashboard: "sales", bod: "sales", pipeline: "sales", contracts: "sales", contractadmin: "sales",
  reconcile: "sales", accounts: "sales",
  bidboard: "estimating", proposals: "estimating", budget: "estimating",
  itbinbox: "estimating",
  pricelist: "estimating", doclibrary: "estimating",
  opsdashboard: "operations", opsprojects: "operations", purchasing: "operations",
  billing: "operations",
  ar: "operations", retention: "operations", closeout: "operations",
  subbuilder: "operations",
  fieldops: "field",
};

// Settings > Users lets an admin pick where each person lands. Field Operations pages are
// URL-routed inside their own app, the rest are host tabs.
const LANDING_TARGETS = {
  "sales-dashboard": ["dashboard"], pipeline: ["pipeline"], contracts: ["contracts"],
  "contract-admin": ["contractadmin"], reconcile: ["reconcile"], accounts: ["accounts"],
  "bid-board": ["bidboard"], "itb-inbox": ["itbinbox"], proposals: ["bidboard"],
  budgets: ["budget"],
  "cost-library": ["pricelist"], "doc-library": ["doclibrary"], subbuilder: ["subbuilder"],
  "ops-dashboard": ["opsdashboard"], "ops-projects": ["opsprojects"],
  purchasing: ["purchasing"],
  "field-dashboard": ["fieldops", "/dashboard"], "field-projects": ["fieldops", "/projects"],
  schedule: ["fieldops", "/schedule"], dispatch: ["fieldops", "/dispatch"],
  map: ["fieldops", "/map"], install: ["fieldops", "/install"],
  warehouse: ["fieldops", "/warehouse"],
  timeclock: ["fieldops", "/timeclock"], timesheets: ["fieldops", "/timesheets"],
};

// Field Operations owns real URLs, so a deep link has to mount the Field Ops shell instead of
// falling through to the dashboard.
const FIELD_DEEP_PATHS = new Set([
  "/dashboard", "/projects", "/schedule", "/dispatch", "/installers",
  "/map", "/install", "/warehouse", "/drawings", "/timeclock", "/timesheets",
]);

const isBidDeepLink = (pathname) => pathname.startsWith("/bids/");
function isFieldDeepLink(pathname) {
  return FIELD_DEEP_PATHS.has(pathname) || pathname === "/mobilizations" || pathname.startsWith("/projects/") || pathname.startsWith("/install/");
}

function tabFromPath(pathname) {
  if (isFieldDeepLink(pathname)) return "fieldops";
  if (isBidDeepLink(pathname)) return "bidboard";
  const hit = Object.entries(TAB_PATH).find(([, to]) => to !== "/" && (pathname === to || pathname.startsWith(`${to}/`)));
  return hit ? hit[0] : "dashboard";
}

function ProtectedApp({ theme, onToggleTheme }) {
  const { user, loading, access, isAdmin, denied, deniedReason, logout, landing, role } = useAuth();
  const routerNavigate = useNavigate();
  const landedRef = useRef(false);
  const deepLink = useRef(window.location.pathname !== "/").current;
  const location = useLocation();
  useEffect(() => { bindNavigate(routerNavigate); }, [routerNavigate]);
  useNavHistory();   // records each history entry's URL for breadcrumbs / the phone arrows
  // The URL is the source of truth for the visible tab; Field Ops pages navigate their own paths.
  const activeTab = tabFromPath(location.pathname);
  const setActiveTab = useCallback((key, opts) => {
    const k = key === "proposals" ? "bidboard" : key;
    if (k === "fieldops" || tabFromPath(window.location.pathname) === k) return;
    if (TAB_PATH[k]) routerNavigate(TAB_PATH[k], opts);
  }, [routerNavigate]);
  // Cross-module navigation (e.g. Operations submittals → SubBuilder) without a router.
  useEffect(() => {
    const jump = (e) => {
      if (e.detail?.path) routerNavigate(e.detail.path);
      else if (e.detail?.sub) routerNavigate(`/settings?sub=${e.detail.sub}`);
      else if (e.detail?.tab) setActiveTab(e.detail.tab);
    };
    window.addEventListener("abs:navigate", jump);
    return () => window.removeEventListener("abs:navigate", jump);
  }, [setActiveTab, routerNavigate]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("abs-sidebar-collapsed") === "true");
  const [navOpen, setNavOpen] = useState(false); // phones only: the nav slides over the page
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navTick, setNavTick] = useState(0);

  // Route audit/email to the Settings shell sub-tabs; everything else is a top-level tab.
  const navigate = useCallback((key) => {
    if (key === "audit") routerNavigate("/settings?sub=audit");
    else setActiveTab(key);
  }, [setActiveTab, routerNavigate]);

  const goToLanding = useCallback((opts) => {
    const [tab, path] = LANDING_TARGETS[landing?.key] || LANDING_TARGETS["sales-dashboard"];
    if (path) routerNavigate(path, opts);
    else setActiveTab(tab, opts);
    setNavTick((n) => n + 1);
  }, [landing, routerNavigate, setActiveTab]);

  // Open on the page this person lands on — once, right after sign-in.
  useEffect(() => {
    if (loading || !landing || landedRef.current) return;
    landedRef.current = true;
    if (deepLink) return;              // a deep link beats the landing preference
    if (landing.key !== "sales-dashboard") goToLanding({ replace: true });
  }, [loading, landing, goToLanding, deepLink]);

  // Global command palette: ⌘K / Ctrl+K toggles it from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Keep the visible tab inside what this role is allowed to see.
  useEffect(() => {
    if (loading || !access) return;
    const allowed = (key) =>
      key === "settings" ? isAdmin : (access[TAB_AREA[key]] || "none") !== "none";
    if (allowed(activeTab)) return;
    const fallback = Object.keys(TAB_AREA).find(allowed);
    if (fallback === "fieldops") routerNavigate("/dashboard", { replace: true });
    else if (fallback) setActiveTab(fallback, { replace: true });
  }, [loading, access, isAdmin, activeTab, setActiveTab, routerNavigate]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("abs-sidebar-collapsed", String(!c));
      return !c;
    });
  };

  // Pipeline state — infinite scroll
  const [pipelineData, setPipelineData] = useState({ projects: [], total: 0, pages: 1, active_count: 0, archived_count: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dataLoading, setDataLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [importing, setImporting] = useState(false);
  const [amFilter, setAmFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState([]);
  const [estFilter, setEstFilter] = useState("ALL");
  // Re-applied each sign-in: AMs open on their own pipeline, everyone else on All.
  useEffect(() => { if (!loading) setAmFilter(defaultAmFor(role, user?.name)); }, [loading, role, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps
  const [customerFilter, setCustomerFilter] = useState([]);
  const [viewMode, setViewMode] = useUrlState("pview", "pipeline");
  const [pipelinePage, setPipelinePage] = useState(1);
  const [pipelineSort, setPipelineSort] = useState({ field: "due_date", dir: "asc" });

  // Debounce search — 300ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => { setPipelinePage(1); }, [debouncedSearch, amFilter, statusFilter, estFilter, customerFilter, viewMode, pipelineSort]);

  // Abort the previous in-flight search when a new one is issued
  const searchAbortRef = useRef(null);

  const fetchProjects = useCallback(async (page = 1, append = false) => {
    if (page === 1) setDataLoading(true);
    else setLoadingMore(true);

    // Cancel any prior request so the latest filter values win
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortRef.current = controller;

    try {
      const params = {
        page,
        limit: 50,
        archived: viewMode === "archive",
        sort_by: pipelineSort.field,
        sort_dir: pipelineSort.dir,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
        delete params.archived;
      }
      if (amFilter !== "ALL") params.am = amFilter;
      if (statusFilter.length > 0) params.status = statusFilter.join(",");
      if (estFilter !== "ALL") params.est = estFilter;
      if (customerFilter.length > 0) params.customer = customerFilter.join("|");

      const data = await api.searchProjects(params, controller.signal);
      if (append) {
        setPipelineData(prev => ({ ...data, projects: [...prev.projects, ...data.projects] }));
      } else {
        setPipelineData(data);
      }
    } catch (e) {
      // Ignore aborts — they're a normal part of fast filter changes
      if (e?.name === "CanceledError" || e?.name === "AbortError" || e?.code === "ERR_CANCELED") {
        return;
      }
      // A deactivated account gets 403 on everything — the access screen says why.
      if (e?.response?.status === 403) {
        return;
      }
      console.error("Failed to fetch projects:", e);
      toast.error("Failed to load projects");
    } finally {
      // Only clear loading if this controller is still the current one
      if (searchAbortRef.current === controller) {
        setDataLoading(false);
        setLoadingMore(false);
      }
    }
  }, [debouncedSearch, amFilter, statusFilter, estFilter, customerFilter, viewMode, pipelineSort]);

  useEffect(() => {
    if (user) fetchProjects(1, false);
  }, [fetchProjects, user]);

  // When page increments beyond 1, append next batch
  useEffect(() => {
    if (pipelinePage > 1) fetchProjects(pipelinePage, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelinePage]);

  const loadMore = useCallback(() => {
    if (!loadingMore && pipelineData.projects.length < pipelineData.total) {
      setPipelinePage(p => p + 1);
    }
  }, [loadingMore, pipelineData.projects.length, pipelineData.total]);

  const refreshProjects = useCallback(() => {
    setPipelinePage(1);
    return fetchProjects(1, false);
  }, [fetchProjects]);

  // Update a single project in local state without refetching
  const updateLocalProject = useCallback((projectId, updatedData) => {
    setPipelineData(prev => ({
      ...prev,
      projects: prev.projects.map(p => p.id === projectId ? { ...p, ...updatedData } : p),
    }));
  }, []);

  // Remove a project from local state without refetching
  const removeLocalProject = useCallback((projectId) => {
    setPipelineData(prev => ({
      ...prev,
      projects: prev.projects.filter(p => p.id !== projectId),
      total: prev.total - 1,
    }));
  }, []);

  const handleImportExcel = async (file) => {
    setImporting(true);
    try {
      const result = await api.importExcel(file);
      toast.success(`${result.detail}`);
      if (result.imported > 0 || result.updated > 0) {
        await refreshProjects();
      }
    } catch (e) {
      console.error("Failed to import Excel:", e);
      const msg = e.response?.data?.detail || "Failed to import Excel file";
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleUpdateProject = async (projectId, updateData) => {
    try {
      const updated = await api.updateProject(projectId, updateData);
      if (updated.archived && viewMode === "pipeline") {
        const statusMsg = updated.status === "Won"
          ? `"${updated.name}" won and moved to Archive. Contract auto-created.`
          : `"${updated.name}" moved to Archive (${updated.status})`;
        toast.info(statusMsg);
        removeLocalProject(projectId);
      } else {
        updateLocalProject(projectId, updated);
      }
    } catch (e) {
      console.error("Failed to update project:", e);
      toast.error("Failed to update project");
    }
  };

  const handleUnarchive = async (projectId) => {
    try {
      const restored = await api.unarchiveProject(projectId);
      toast.success(`"${restored.name}" restored to Pipeline`);
      await refreshProjects();
    } catch (e) {
      console.error("Failed to unarchive project:", e);
      toast.error("Failed to restore project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await api.deleteProject(projectId);
      toast.success("Project permanently deleted");
      removeLocalProject(projectId);
    } catch (e) {
      console.error("Failed to delete project:", e);
      toast.error("Failed to delete project");
    }
  };

  const handleBatchUpdateStatus = async (ids, status) => {
    const results = await Promise.allSettled(
      ids.map(id => api.updateProject(id, { status }))
    );
    const successes = results.filter(r => r.status === "fulfilled").length;
    const failures = results.length - successes;
    if (failures === 0) {
      toast.success(`Updated ${successes} project${successes > 1 ? "s" : ""} to "${status}"`);
    } else if (successes === 0) {
      console.error("Batch update failed:", results);
      toast.error(`Failed to update ${failures} project${failures > 1 ? "s" : ""}`);
    } else {
      console.error("Partial batch update failure:", results);
      toast.error(`Updated ${successes}, ${failures} failed`);
    }
    await refreshProjects();
  };

  const handleBatchArchive = async (ids) => {
    try {
      const result = await api.batchArchiveProjects(ids);
      toast.success(`Archived ${result.archived_count} project${result.archived_count > 1 ? "s" : ""}`);
      await refreshProjects();
    } catch (e) {
      console.error("Batch archive failed:", e);
      toast.error("Failed to archive projects");
      await refreshProjects();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-700 rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (denied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6"
           data-testid="access-denied">
        <div className="max-w-md text-center">
          <img src="/abs-logo.png" alt="ABS" className="mx-auto mb-6 h-10 w-auto invert opacity-90" />
          <h1 className="text-2xl font-semibold text-white">Access has been turned off</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            {deniedReason ? `${deniedReason}.` : "This account is not active in ContractsApp."} Ask
            an administrator to switch your account back on in Settings → Users.
          </p>
          <button onClick={logout}
                  className="mt-7 rounded-full bg-white/10 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20"
                  data-testid="access-denied-signout">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-[#F8FAFC]" data-testid="app-root">
      {/* Phones get the nav as an overlay so the drawing and field views keep the full screen. */}
      {navOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" data-testid="nav-backdrop"
             onClick={() => setNavOpen(false)} />
      )}
      <Sidebar
        mobileOpen={navOpen}
        activeTab={activeTab}
        onTabChange={(key) => { setActiveTab(key); setNavTick((n) => n + 1); setNavOpen(false); }}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        onOpenSearch={() => setPaletteOpen(true)}
        onGoHome={() => goToLanding()}
      />

      <div className="flex-1 overflow-hidden flex flex-col min-w-0">
        <PageHeader tab={activeTab} onOpenNav={() => setNavOpen(true)} />
        {activeTab === "dashboard" && <DashboardPage />}

        {activeTab === "bod" && <BasisOfDesignPage />}
        {activeTab === "pipeline" && (
          <>
            <div className="page-pane gap-3">
            <PipelineHeader
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onImportExcel={handleImportExcel}
              projectCount={pipelineData.total}
              activeCount={pipelineData.active_count}
              importing={importing}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              archivedCount={pipelineData.archived_count}
              sumTotalSales={pipelineData.sum_total_sales || 0}
              sumForecasted={pipelineData.sum_forecasted || 0}
              amFilter={amFilter}
              onAmFilterChange={setAmFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              allAms={pipelineData.all_ams || []}
              allStatuses={pipelineData.all_statuses || []}
            />
            <div className="pane-card border border-slate-200 bg-white">
            {dataLoading && !pipelineData.projects.length ? (
              <div className="flex-1 flex items-center justify-center" data-testid="loading-state">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-8 w-8 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-slate-500">Loading pipeline...</span>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden bg-white flex flex-col">
                <PipelineTable
                  estFilter={estFilter}
                  onEstFilterChange={setEstFilter}
                  customerFilter={customerFilter}
                  onCustomerFilterChange={setCustomerFilter}
                  allEsts={pipelineData.all_ests || []}
                  allCustomers={pipelineData.all_customers || []}
                  data={pipelineData.projects}
                  onUpdateProject={handleUpdateProject}
                  onBatchUpdateStatus={handleBatchUpdateStatus}
                  onBatchArchive={handleBatchArchive}
                  isArchiveView={viewMode === "archive"}
                  onUnarchive={handleUnarchive}
                  onDeleteProject={handleDeleteProject}
                  sortState={pipelineSort}
                  onSortChange={setPipelineSort}
                  onRefreshProjects={refreshProjects}
                  onLoadMore={loadMore}
                  loadingMore={loadingMore}
                  totalCount={pipelineData.total}
                  onLocalUpdate={updateLocalProject}
                />
              </div>
            )}
            </div></div>
          </>
        )}

        {activeTab === "contracts" && <ContractsPage />}
        {activeTab === "contractadmin" && <ContractAdminApp />}
        {activeTab === "reconcile" && <ReconcilePage />}
        {activeTab === "accounts" && <AccountsPage />}
        {activeTab === "settings" && <SettingsShell />}
        {activeTab === "budget" && <BudgetApp />}
        {activeTab === "pricelist" && <PriceListPage />}
        {activeTab === "bidboard" && <BidBoardPage />}
        {activeTab === "itbinbox" && <ItbInboxPage />}
        {activeTab === "opsprojects" && <OperationsProjectsPage />}
        {activeTab === "opsdashboard" && <OpsDashboardPage />}
        {activeTab === "purchasing" && <PurchasingPage />}
        {activeTab === "billing" && <BillingDashboardPage />}
        {activeTab === "ar" && <ReceivablesPage />}
        {activeTab === "retention" && <RetentionPage />}
        {activeTab === "closeout" && <CloseoutPage />}
        {activeTab === "subbuilder" && <SubBuilderApp />}
        {activeTab === "fieldops" && <FieldOpsApp />}
        {ESTIMATING_STUBS[activeTab] && (
          <EstimatingStub tabKey={activeTab} />
        )}
      </div>
      <MiraWidget />
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onNavigate={navigate} />
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("abs-theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("abs-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Log build version on mount for deployment verification
  useEffect(() => {
    console.log("[ABS] Build: v4-fetch-public-20260409");
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" offset={64} richColors theme={theme} />
        <KeyboardNav />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={<ProtectedApp theme={theme} onToggleTheme={toggleTheme} />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
